// src/pages/Settings.tsx
import React, { useState } from 'react';
import { 
  Settings as SettingsIcon,
  Bell,
  Shield,
  Globe,
  Moon,
  Download,
  Trash2,
  Save,
  Eye,
  EyeOff,
  User,
  Lock,
  Mail,
  Smartphone
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [settings, setSettings] = useState({
    // General Settings
    language: 'en',
    timezone: 'Africa/Nairobi',
    dateFormat: 'DD/MM/YYYY',
    currency: 'KES',
    
    // Notification Settings
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    reportUpdates: true,
    campaignAlerts: true,
    systemAlerts: true,
    
    // Privacy Settings
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    dataSharing: false,
    
    // Security Settings
    twoFactorAuth: false,
    sessionTimeout: 30,
    loginAlerts: true,
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const tabs = [
    { id: 'general', label: 'General', icon: <SettingsIcon size={18} /> },
    { id: 'notifications', label: 'Notifications', icon: <Bell size={18} /> },
    { id: 'privacy', label: 'Privacy', icon: <Shield size={18} /> },
    { id: 'security', label: 'Security', icon: <Lock size={18} /> },
    { id: 'appearance', label: 'Appearance', icon: <Moon size={18} /> },
    { id: 'data', label: 'Data & Export', icon: <Download size={18} /> },
  ];

  const handleSaveSettings = () => {
    toast.success('Settings saved successfully');
  };

  const handleChangePassword = () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    toast.success('Password changed successfully');
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    });
  };

  const handleExportData = () => {
    toast.success('Data export initiated. You will receive an email with your data.');
  };

  const handleDeleteAccount = () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      toast.success('Account deletion request submitted');
    }
  };

  const SettingToggle = ({ label, description, checked, onChange, icon: Icon }: any) => (
    <div className="flex items-center justify-between py-4 border-b border-gray-200">
      <div className="flex items-start">
        {Icon && <Icon className="text-gray-400 mt-1 mr-3" size={20} />}
        <div>
          <label className="block text-sm font-medium text-gray-900">{label}</label>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
      <button
        onClick={onChange}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
          checked ? 'bg-[#3c8d2e]' : 'bg-gray-200'
        }`}
      >
        <span
          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );

  const SettingSelect = ({ label, description, value, options, onChange, icon: Icon }: any) => (
    <div className="py-4 border-b border-gray-200">
      <div className="flex items-start mb-2">
        {Icon && <Icon className="text-gray-400 mt-1 mr-3" size={20} />}
        <div>
          <label className="block text-sm font-medium text-gray-900">{label}</label>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#3c8d2e] focus:outline-none focus:ring-2 focus:ring-[#3c8d2e]"
      >
        {options.map((option: any) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return (
          <div className="space-y-4">
            <SettingSelect
              label="Language"
              description="Choose your preferred language"
              icon={Globe}
              value={settings.language}
              onChange={(value: string) => setSettings({ ...settings, language: value })}
              options={[
                { value: 'en', label: 'English' },
                { value: 'sw', label: 'Kiswahili' },
              ]}
            />
            
            <SettingSelect
              label="Timezone"
              description="Set your local timezone"
              icon={Globe}
              value={settings.timezone}
              onChange={(value: string) => setSettings({ ...settings, timezone: value })}
              options={[
                { value: 'Africa/Nairobi', label: 'Nairobi, Kenya (GMT+3)' },
                { value: 'UTC', label: 'UTC' },
              ]}
            />
            
            <SettingSelect
              label="Date Format"
              description="Choose how dates are displayed"
              value={settings.dateFormat}
              onChange={(value: string) => setSettings({ ...settings, dateFormat: value })}
              options={[
                { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY' },
                { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY' },
                { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD' },
              ]}
            />
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-[#0b2045] uppercase tracking-wider mb-2">
              Notification Channels
            </h3>
            
            <SettingToggle
              label="Email Notifications"
              description="Receive notifications via email"
              icon={Mail}
              checked={settings.emailNotifications}
              onChange={() => setSettings({ ...settings, emailNotifications: !settings.emailNotifications })}
            />
            
            <SettingToggle
              label="SMS Notifications"
              description="Receive notifications via SMS"
              icon={Smartphone}
              checked={settings.smsNotifications}
              onChange={() => setSettings({ ...settings, smsNotifications: !settings.smsNotifications })}
            />
            
            <h3 className="text-sm font-semibold text-[#0b2045] uppercase tracking-wider mt-6 mb-2">
              Notification Types
            </h3>
            
            <SettingToggle
              label="Report Updates"
              description="Get notified when your reports are updated"
              icon={Bell}
              checked={settings.reportUpdates}
              onChange={() => setSettings({ ...settings, reportUpdates: !settings.reportUpdates })}
            />
            
            <SettingToggle
              label="Campaign Alerts"
              description="Receive alerts about new campaigns in your area"
              icon={Bell}
              checked={settings.campaignAlerts}
              onChange={() => setSettings({ ...settings, campaignAlerts: !settings.campaignAlerts })}
            />
            
            <SettingToggle
              label="System Alerts"
              description="Important system announcements and updates"
              icon={Bell}
              checked={settings.systemAlerts}
              onChange={() => setSettings({ ...settings, systemAlerts: !settings.systemAlerts })}
            />
          </div>
        );

      case 'privacy':
        return (
          <div className="space-y-4">
            <SettingSelect
              label="Profile Visibility"
              description="Control who can see your profile information"
              icon={User}
              value={settings.profileVisibility}
              onChange={(value: string) => setSettings({ ...settings, profileVisibility: value })}
              options={[
                { value: 'public', label: 'Public (Anyone)' },
                { value: 'citizens', label: 'Citizens Only' },
                { value: 'private', label: 'Private (Only Me)' },
              ]}
            />
            
            <SettingToggle
              label="Show Email Address"
              description="Allow others to see your email address"
              icon={Mail}
              checked={settings.showEmail}
              onChange={() => setSettings({ ...settings, showEmail: !settings.showEmail })}
            />
            
            <SettingToggle
              label="Show Phone Number"
              description="Allow others to see your phone number"
              icon={Smartphone}
              checked={settings.showPhone}
              onChange={() => setSettings({ ...settings, showPhone: !settings.showPhone })}
            />
            
            <SettingToggle
              label="Data Sharing"
              description="Allow anonymous data sharing for research purposes"
              icon={Shield}
              checked={settings.dataSharing}
              onChange={() => setSettings({ ...settings, dataSharing: !settings.dataSharing })}
            />
          </div>
        );

      case 'security':
        return (
          <div className="space-y-6">
            {/* Password Change */}
            <div className="bg-[#f4f7f6] rounded-lg p-4 border border-[#0b2045]/10">
              <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Change Password</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={passwordForm.currentPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <button
                  onClick={handleChangePassword}
                  className="w-full bg-[#0b2045] text-white py-2 px-4 rounded-lg hover:bg-[#152c53] transition-colors"
                >
                  Update Password
                </button>
              </div>
            </div>
            
            {/* Two-Factor Authentication */}
            <SettingToggle
              label="Two-Factor Authentication"
              description="Add an extra layer of security to your account"
              icon={Shield}
              checked={settings.twoFactorAuth}
              onChange={() => setSettings({ ...settings, twoFactorAuth: !settings.twoFactorAuth })}
            />
            
            {/* Session Management */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Session Timeout
              </label>
              <select
                value={settings.sessionTimeout}
                onChange={(e) => setSettings({ ...settings, sessionTimeout: parseInt(e.target.value) })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3c8d2e] focus:border-transparent"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
              </select>
              <p className="text-sm text-gray-500 mt-2">
                Automatically log out after inactivity
              </p>
            </div>
            
            <SettingToggle
              label="Login Alerts"
              description="Get notified when someone logs into your account"
              icon={Bell}
              checked={settings.loginAlerts}
              onChange={() => setSettings({ ...settings, loginAlerts: !settings.loginAlerts })}
            />
          </div>
        );

      case 'appearance':
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-[#0b2045]">Dark Mode</h3>
                <p className="text-sm text-gray-500">Switch between light and dark themes</p>
              </div>
              <button className="px-4 py-2 bg-[#f4f7f6] text-[#0b2045] rounded-lg hover:bg-[#e8f1ec] transition-colors">
                Toggle Theme
              </button>
            </div>
            
            <div>
              <h3 className="text-sm font-medium text-[#0b2045] mb-2">Font Size</h3>
              <div className="flex space-x-2">
                {['Small', 'Medium', 'Large'].map((size) => (
                  <button
                    key={size}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-[#f4f7f6] transition-colors"
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
          </div>
        );

      case 'data':
        return (
          <div className="space-y-6">
            {/* Data Export */}
            <div className="bg-[#0b2045]/10 border border-[#0b2045]/20 rounded-lg p-4">
              <div className="flex items-start">
                <Download className="text-[#0b2045] mt-1 mr-3" size={20} />
                <div>
                  <h3 className="text-lg font-semibold text-[#0b2045]">Export Your Data</h3>
                  <p className="text-sm text-[#0b2045]/80 mt-1">
                    Download a copy of all your data including reports, messages, and account information.
                  </p>
                  <button
                    onClick={handleExportData}
                    className="mt-3 px-4 py-2 bg-[#0b2045] text-white rounded-lg hover:bg-[#152c53] transition-colors"
                  >
                    Request Data Export
                  </button>
                </div>
              </div>
            </div>
            
            {/* Account Deletion */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start">
                <Trash2 className="text-red-500 mt-1 mr-3" size={20} />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Delete Account</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Permanently delete your account and all associated data. This action cannot be undone.
                  </p>
                  <button
                    onClick={handleDeleteAccount}
                    className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    Delete My Account
                  </button>
                </div>
              </div>
            </div>
            
            {/* Data Usage */}
            <div>
              <h3 className="text-lg font-semibold text-[#0b2045] mb-4">Data Usage</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Reports</span>
                  <span className="font-medium text-[#0b2045]">24 reports</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Messages Sent</span>
                  <span className="font-medium text-[#0b2045]">156 messages</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Storage Used</span>
                  <span className="font-medium text-[#0b2045]">45 MB</span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0b2045]">Settings</h1>
        <p className="text-gray-600 mt-2">Manage your account settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Sidebar */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-200">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-[#0b2045]/10 text-[#0b2045]'
                      : 'text-gray-700 hover:bg-[#f4f7f6]'
                  }`}
                >
                  <span className="mr-3">{tab.icon}</span>
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>

            {/* Save Button */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={handleSaveSettings}
                className="w-full flex items-center justify-center px-4 py-2 bg-[#3c8d2e] text-white rounded-lg hover:bg-[#2d7022] transition-colors"
              >
                <Save size={16} className="mr-2" />
                Save All Changes
              </button>
            </div>
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <h2 className="text-lg font-semibold text-[#0b2045] mb-6">
              {tabs.find(t => t.id === activeTab)?.label} Settings
            </h2>
            
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;